/**
 * Brain Health Points System (BHPS) Scoring Engine.
 * Ported from Python bhps_engine.py
 *
 * 100 points across 8 pathways, modeled on WELL v2 structure.
 * 6 mandatory preconditions + 47 features earning 1-3 points each.
 */

// Pathway caps
export const PATHWAY_CAPS = {
  daylight: 15,
  noise: 15,
  enrichment: 15,
  nature: 12,
  movement: 12,
  visual: 11,
  air_quality: 10,
  wayfinding: 10,
};

// Preconditions (mandatory, 0 pts)
export const PRECONDITIONS = [
  { id: 'P1', pathway: 'daylight', label: 'Minimum daylight >=150 lux at task height', threshold: '>=150 lux' },
  { id: 'P2', pathway: 'noise', label: 'Noise safety <80 dB sustained', threshold: '<80 dB' },
  { id: 'P3', pathway: 'air_quality', label: 'Basic air quality PM2.5 <35 ug/m3', threshold: '<35 ug/m3' },
  { id: 'P4', pathway: 'visual', label: 'Visual safety pattern stress <0.75', threshold: '<0.75' },
  { id: 'P5', pathway: 'wayfinding', label: 'Emergency wayfinding: exit signs visible', threshold: 'manual' },
  { id: 'P6', pathway: 'movement', label: 'Accessible circulation: ADA-compliant paths', threshold: 'manual' },
];

// Feature catalog (47 features)
export const FEATURES = [
  // Daylight (15 pts)
  { id: 'D1', pathway: 'daylight', points: 3, label: 'Ambient light >=400 lux at task height', autoCheck: s => s.daylight_lux >= 400 },
  { id: 'D2', pathway: 'daylight', points: 3, label: 'Ambient light >=500 lux or direct sky view', autoCheck: s => s.daylight_lux >= 500 },
  { id: 'D3', pathway: 'daylight', points: 2, label: 'Tunable color temperature (2700-5000K)', autoCheck: null },
  { id: 'D4', pathway: 'daylight', points: 2, label: 'Circadian-aligned lighting schedule', autoCheck: null },
  { id: 'D5', pathway: 'daylight', points: 2, label: 'Dedicated task lighting >=500 lux', autoCheck: null },
  { id: 'D6', pathway: 'daylight', points: 1, label: 'Light therapy station available', autoCheck: null },
  { id: 'D7', pathway: 'daylight', points: 2, label: 'Automated daylight-responsive dimming', autoCheck: null },
  // Noise (15 pts)
  { id: 'N1', pathway: 'noise', points: 3, label: 'Ambient noise <=65 dB during typical use', autoCheck: s => s.noise_db <= 65 },
  { id: 'N2', pathway: 'noise', points: 3, label: 'Ambient noise <=55 dB during typical use', autoCheck: s => s.noise_db <= 55 },
  { id: 'N3', pathway: 'noise', points: 3, label: 'Acoustic ceiling (NRC >=0.75, >=60% coverage)', autoCheck: null },
  { id: 'N4', pathway: 'noise', points: 2, label: 'Acoustic wall panels (>=20% wall area)', autoCheck: null },
  { id: 'N5', pathway: 'noise', points: 2, label: 'Soft floor covering (>=50% floor area)', autoCheck: null },
  { id: 'N6', pathway: 'noise', points: 1, label: 'Sound masking system', autoCheck: null },
  { id: 'N7', pathway: 'noise', points: 1, label: 'Upholstered/acoustic furniture', autoCheck: null },
  // Nature/Biophilia (12 pts)
  { id: 'B1', pathway: 'nature', points: 2, label: 'Indoor plants >=1 per 100 sq ft', autoCheck: null },
  { id: 'B2', pathway: 'nature', points: 2, label: 'Living wall or green feature >=20 sq ft', autoCheck: null },
  { id: 'B3', pathway: 'nature', points: 3, label: 'Direct nature view from >=50% of seating', autoCheck: s => s.nature_view },
  { id: 'B4', pathway: 'nature', points: 2, label: 'Natural materials (wood, stone) in primary finishes', autoCheck: null },
  { id: 'B5', pathway: 'nature', points: 1, label: 'Water feature present', autoCheck: null },
  { id: 'B6', pathway: 'nature', points: 1, label: 'Nature imagery or murals', autoCheck: null },
  { id: 'B7', pathway: 'nature', points: 1, label: 'Biomorphic/organic design forms', autoCheck: null },
  // Enrichment (15 pts)
  { id: 'E1', pathway: 'enrichment', points: 2, label: 'Visual interest: artwork, color, focal points', autoCheck: null },
  { id: 'E2', pathway: 'enrichment', points: 3, label: 'Spatial diversity: >=2 distinct activity zones', autoCheck: null },
  { id: 'E3', pathway: 'enrichment', points: 2, label: 'Sensory variety: varied textures, sounds, scents', autoCheck: null },
  { id: 'E4', pathway: 'enrichment', points: 3, label: 'Social affordances: seating for 2, 4, and groups', autoCheck: null },
  { id: 'E5', pathway: 'enrichment', points: 2, label: 'Novelty: elements rotate monthly or more often', autoCheck: null },
  { id: 'E6', pathway: 'enrichment', points: 3, label: 'Cognitive affordances: games, books, puzzles, activities', autoCheck: null },
  // Movement (12 pts)
  { id: 'M1', pathway: 'movement', points: 2, label: 'Walking route >=50m to/within space', autoCheck: s => s.movement_minutes > 0 },
  { id: 'M2', pathway: 'movement', points: 3, label: 'Visible, attractive stairway nearby', autoCheck: null },
  { id: 'M3', pathway: 'movement', points: 2, label: 'Standing-height surfaces available', autoCheck: null },
  { id: 'M4', pathway: 'movement', points: 2, label: 'Flexible/moveable furniture', autoCheck: null },
  { id: 'M5', pathway: 'movement', points: 2, label: 'Activity or fitness space within 2 min walk', autoCheck: null },
  { id: 'M6', pathway: 'movement', points: 1, label: 'Direct outdoor access within 2 min', autoCheck: s => s.nature_exposure },
  // Air Quality (10 pts)
  { id: 'A1', pathway: 'air_quality', points: 2, label: 'PM2.5 <15 ug/m3 annual average', autoCheck: s => s.pm25_ug_m3 < 15 },
  { id: 'A2', pathway: 'air_quality', points: 2, label: 'PM2.5 <8 ug/m3 annual average', autoCheck: s => s.pm25_ug_m3 < 8 },
  { id: 'A3', pathway: 'air_quality', points: 2, label: 'MERV 13+ HVAC filtration', autoCheck: null },
  { id: 'A4', pathway: 'air_quality', points: 1, label: 'Portable HEPA air purifier(s) in space', autoCheck: null },
  { id: 'A5', pathway: 'air_quality', points: 2, label: 'Source isolation (kitchen positive pressure)', autoCheck: null },
  { id: 'A6', pathway: 'air_quality', points: 1, label: 'Visible air quality monitor in space', autoCheck: null },
  // Wayfinding (10 pts)
  { id: 'W1', pathway: 'wayfinding', points: 2, label: 'Clear directional signage at decision points', autoCheck: null },
  { id: 'W2', pathway: 'wayfinding', points: 2, label: 'Distinctive landmarks at key junctions', autoCheck: null },
  { id: 'W3', pathway: 'wayfinding', points: 2, label: 'Visual sightlines to destinations', autoCheck: s => s.wayfinding_score >= 0.7 },
  { id: 'W4', pathway: 'wayfinding', points: 2, label: 'Simple, legible floor plan', autoCheck: null },
  { id: 'W5', pathway: 'wayfinding', points: 1, label: 'Color or material coding by area', autoCheck: null },
  { id: 'W6', pathway: 'wayfinding', points: 1, label: 'You-are-here maps at entries', autoCheck: null },
  // Visual Comfort (11 pts)
  { id: 'V1', pathway: 'visual', points: 3, label: 'No uniform grid patterns on major surfaces', autoCheck: s => s.visual_pattern_stress < 0.3 },
  { id: 'V2', pathway: 'visual', points: 2, label: 'Natural/organic patterns on surfaces', autoCheck: null },
  { id: 'V3', pathway: 'visual', points: 2, label: 'Curved or varied geometry elements', autoCheck: null },
  { id: 'V4', pathway: 'visual', points: 2, label: 'Non-reflective, diffuse surface materials', autoCheck: null },
  { id: 'V5', pathway: 'visual', points: 2, label: 'Non-uniform lighting layout', autoCheck: null },
];

export const FEATURE_MAP = Object.fromEntries(FEATURES.map(f => [f.id, f]));

/**
 * Check all 6 preconditions against a space.
 * @param {Object} space - Space data with measurement fields
 * @returns {Object} { P1: true/false, ... }
 */
export function checkPreconditions(space) {
  return {
    P1: (space.daylight_lux || 0) >= 150,
    P2: (space.noise_db || 999) < 80,
    P3: (space.pm25_ug_m3 || 999) < 35,
    P4: (space.visual_pattern_stress || 0) < 0.75,
    P5: true, // Manual
    P6: true, // Manual
  };
}

/**
 * Auto-detect features from space measurements.
 * @param {Object} space - Space data
 * @returns {Object} { featureId: true/false }
 */
export function autoDetectFeatures(space) {
  const detected = {};
  for (const f of FEATURES) {
    if (f.autoCheck) {
      try {
        detected[f.id] = f.autoCheck(space);
      } catch {
        detected[f.id] = false;
      }
    }
  }
  return detected;
}

/**
 * Score a single space.
 * @param {Object} space - Space data
 * @param {Object} featureStatuses - { featureId: 'earned'|'planned'|'not_pursuing' }
 * @returns {Object} SpaceScore
 */
export function scoreSpace(space, featureStatuses = {}) {
  const preconditions = checkPreconditions(space);
  const allPass = Object.values(preconditions).every(v => v);
  const auto = autoDetectFeatures(space);

  const pathwayPoints = {};
  for (const pw of Object.keys(PATHWAY_CAPS)) {
    pathwayPoints[pw] = 0;
  }

  const features = FEATURES.map(f => {
    const manualStatus = featureStatuses[f.id] || 'not_pursuing';
    const autoResult = auto[f.id];

    let status, autoDetected;
    if (manualStatus === 'earned') {
      status = 'earned';
      autoDetected = false;
    } else if (autoResult === true) {
      status = 'earned';
      autoDetected = true;
    } else {
      status = manualStatus;
      autoDetected = false;
    }

    let earnedPts = status === 'earned' ? f.points : 0;
    // Enforce pathway cap
    if (pathwayPoints[f.pathway] + earnedPts > PATHWAY_CAPS[f.pathway]) {
      earnedPts = Math.max(0, PATHWAY_CAPS[f.pathway] - pathwayPoints[f.pathway]);
    }
    pathwayPoints[f.pathway] += earnedPts;

    return {
      featureId: f.id,
      status,
      points: earnedPts,
      label: f.label,
      pathway: f.pathway,
      autoDetected,
    };
  });

  const totalPoints = Object.values(pathwayPoints).reduce((a, b) => a + b, 0);
  const tier = allPass ? getCertificationTier(totalPoints) : 'none';

  return {
    spaceId: space.id,
    spaceName: space.name,
    preconditionsPassed: allPass,
    preconditionResults: preconditions,
    totalPoints,
    pathwayPoints,
    pathwayCaps: PATHWAY_CAPS,
    tier,
    features,
  };
}

/**
 * Score an entire project (WELL/LEED-style tiered-threshold scoring).
 *
 * Scoring rules modeled on WELL v2 + LEED v4.1:
 *
 * PRECONDITIONS: Must pass in ALL spaces. If any space fails a precondition,
 * the project cannot earn certification (same as WELL v2).
 *
 * FEATURES with autoCheck (measurement-based, per-space):
 *   These require compliance across spaces, using LEED-style tiers:
 *   - >=75% of spaces comply → full points
 *   - >=50% of spaces comply → half points (rounded up)
 *   - <50% of spaces comply → 0 points
 *   This prevents a single good space from earning project-wide credit.
 *
 * FEATURES without autoCheck (manual/design features):
 *   These are typically building-wide installations (tunable lighting,
 *   acoustic panels, MERV filtration). Earned at project level — if marked
 *   "earned" in ANY space, it counts for the project.
 *
 * @param {Array} spacesWithFeatures - Array of { space, featureStatuses }
 * @returns {Object} ProjectScore
 */
export function scoreProject(spacesWithFeatures) {
  const numSpaces = spacesWithFeatures.length;
  if (numSpaces === 0) {
    return {
      totalPoints: 0, pathwayPoints: {}, pathwayCaps: PATHWAY_CAPS,
      tier: 'none', allPreconditionsPass: false, projectFeatures: [],
      spaceScores: [], avgScore: 0,
    };
  }

  // Score each space individually (for per-space diagnostics)
  const spaceScores = spacesWithFeatures.map(({ space, featureStatuses }) =>
    scoreSpace(space, featureStatuses)
  );

  // Preconditions: must pass in ALL spaces
  const allPreconditionsPass = spaceScores.every(ss => ss.preconditionsPassed);

  // For each feature, count how many spaces earned it
  const featureEarnedCounts = {};
  for (const f of FEATURES) {
    featureEarnedCounts[f.id] = spaceScores.filter(ss => {
      const sf = ss.features.find(feat => feat.featureId === f.id);
      return sf && sf.status === 'earned';
    }).length;
  }

  // Accumulate project-level pathway points with caps
  const pathwayPoints = {};
  for (const pw of Object.keys(PATHWAY_CAPS)) {
    pathwayPoints[pw] = 0;
  }

  const projectFeatures = FEATURES.map(f => {
    const earnedCount = featureEarnedCounts[f.id] || 0;
    const pctEarned = earnedCount / numSpaces;
    const isAutoDetectable = f.autoCheck !== null;

    let earnedPts = 0;
    let complianceLevel = 'none'; // none, partial, full

    if (isAutoDetectable) {
      // Measurement-based feature: tiered threshold (LEED-style)
      if (pctEarned >= 0.75) {
        earnedPts = f.points;
        complianceLevel = 'full';
      } else if (pctEarned >= 0.50) {
        earnedPts = Math.ceil(f.points / 2);
        complianceLevel = 'partial';
      }
      // <50% → 0 points
    } else {
      // Manual/design feature: project-level (earned in ANY space)
      if (earnedCount > 0) {
        earnedPts = f.points;
        complianceLevel = 'full';
      }
    }

    // Enforce pathway cap
    if (pathwayPoints[f.pathway] + earnedPts > PATHWAY_CAPS[f.pathway]) {
      earnedPts = Math.max(0, PATHWAY_CAPS[f.pathway] - pathwayPoints[f.pathway]);
    }
    pathwayPoints[f.pathway] += earnedPts;

    return {
      featureId: f.id,
      label: f.label,
      pathway: f.pathway,
      maxPoints: f.points,
      earnedPts,
      earnedCount,
      totalSpaces: numSpaces,
      pctCompliant: Math.round(pctEarned * 100),
      complianceLevel,
      isAutoDetectable,
    };
  });

  const totalPoints = Object.values(pathwayPoints).reduce((a, b) => a + b, 0);
  const tier = allPreconditionsPass ? getCertificationTier(totalPoints) : 'none';

  return {
    totalPoints,
    pathwayPoints,
    pathwayCaps: PATHWAY_CAPS,
    tier,
    allPreconditionsPass,
    projectFeatures,
    spaceScores,
    avgScore: totalPoints,
  };
}

/**
 * Get certification tier from score.
 */
export function getCertificationTier(score) {
  score = Math.round(score);
  if (score >= 75) return 'platinum';
  if (score >= 60) return 'gold';
  if (score >= 45) return 'silver';
  if (score >= 30) return 'bronze';
  return 'none';
}
